import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

interface Review {
  id: number;
  name: string;
  location: string;
  message: string;
  rating: number;
  tour: string;
}

interface ClientReviewsProps {
  className?: string;
}

const reviews: Review[] = [
  {
    id: 1,
    name: "Sarah Johnson",
    location: "United States",
    message: "The Murchison Falls tour was absolutely incredible! Our guide was knowledgeable and friendly, making the experience unforgettable.",
    rating: 5,
    tour: "3-Day Murchison Falls Adventure"
  },
  {
    id: 2,
    name: "David Chen",
    location: "Singapore",
    message: "Amazing gorilla trekking experience in Bwindi. Everything was well-organized and the accommodations were perfect.",
    rating: 5,
    tour: "Bwindi Gorilla Trek"
  },
  {
    id: 3,
    name: "Emma Williams",
    location: "UK",
    message: "The Tanzania safari exceeded all our expectations. Saw the big five and the migration. Truly a once-in-a-lifetime experience!",
    rating: 5,
    tour: "Tanzania Safari Adventure"
  },
  {
    id: 4,
    name: "Michael Peters",
    location: "Canada",
    message: "Professional service from start to finish. The team went above and beyond to ensure we had the best safari experience.",
    rating: 5,
    tour: "Kenya Wildlife Safari"
  }
];

export default function ClientReviews({ className = "" }: ClientReviewsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 8000); 

    return () => clearInterval(timer);
  }, []);

        return (
          <section className={`py-16 relative overflow-hidden bg-white ${className}`}>
            <div className="absolute inset-0 bg-gray-20">
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: `radial-gradient(circle, #333 1px, transparent 2px)`,
                  backgroundSize: "20px 20px",
                  backgroundRepeat: "repeat"
                }}
              ></div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl font-bold mb-4">{t('whatOurClientsSay')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('clientReviewsSubtitle')}
          </p>
        </motion.div>

        <div className="relative h-[250px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0"
            >
              <div className="bg-white rounded-xl shadow-lg p-6 mx-auto max-w-2xl relative">
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(reviews[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <p className="text-lg text-gray-700 italic mb-6">
                  "{reviews[currentIndex].message}"
                </p>

                <div className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-semibold text-gray-900">{reviews[currentIndex].name}</p>
                    <p className="text-gray-500">{reviews[currentIndex].location}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{t('tour')}</p>
                    <p className="text-gray-500">{reviews[currentIndex].tour}</p>
                  </div>
                </div>

                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {reviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentIndex ? "bg-primary w-4" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}